import { useState } from "react";
import type { AgentSettings } from "../../shared/contracts";
import { errorText } from "../lib/api";
import { fmtSnoozeUntil, isSnoozed, nextMorning, oneHourFrom, setSnooze } from "../lib/snooze";
import { Button, cx } from "./ui";

/**
 * Pause of proactive messages: « 1 h » and « Demain matin »; while paused, until when and « Reprendre ».
 * `compact` shows only « Reprendre » while paused.
 */
export function SnoozeControls({
  settings,
  now,
  compact = false,
  onChange,
  className,
}: {
  settings: AgentSettings;
  now: number;
  compact?: boolean;
  onChange?: (settings: AgentSettings) => void;
  className?: string;
}) {
  const [busy, setBusy] = useState<"hour" | "morning" | "resume" | null>(null);
  const [error, setError] = useState<string | null>(null);
  const paused = isSnoozed(settings, now);

  async function run(kind: "hour" | "morning" | "resume") {
    if (busy) return;
    setBusy(kind);
    setError(null);
    try {
      const t = Date.now();
      const until = kind === "hour" ? oneHourFrom(t) : kind === "morning" ? nextMorning(settings, t) : null;
      const next = await setSnooze(until);
      onChange?.(next);
    } catch (err) {
      setError(errorText(err));
    } finally {
      setBusy(null);
    }
  }

  return (
    <div className={cx("min-w-0", className)}>
      <div className="flex flex-wrap items-center justify-end gap-2">
        {paused && settings.snoozeUntil ? <span className="text-[13px] text-accent">Jusqu'à {fmtSnoozeUntil(settings.snoozeUntil)}</span> : null}
        {paused ? (
          <Button size="sm" variant="secondary" busy={busy === "resume"} disabled={busy !== null} onClick={() => void run("resume")}>
            Reprendre
          </Button>
        ) : null}
        {!compact && !paused ? (
          <>
            <Button size="sm" variant="secondary" busy={busy === "hour"} disabled={busy !== null} onClick={() => void run("hour")} aria-label="Pause 1 h" className="min-w-11">
              1 h
            </Button>
            <Button size="sm" variant="secondary" busy={busy === "morning"} disabled={busy !== null} onClick={() => void run("morning")} aria-label="Pause jusqu'à demain matin">
              Demain matin
            </Button>
          </>
        ) : null}
      </div>
      {error ? (
        <p className="mt-2 text-[12.5px] text-danger" role="alert">
          {error}
        </p>
      ) : null}
    </div>
  );
}
